import { inject, Injectable } from '@angular/core';
import { CaseRootInstance } from '../interfaces/case-instance.interface';
import {
    Action,
    CustomCodeEventDetail,
    InboxType,
    ReassignmentTableOptions,
} from '../interfaces/reassignment.interface';
import { TableColumnConfig } from '../interfaces/table-builder.interface';
import { BulkReassignmentRequest } from './bulk-operation.service';
import { MappingService } from './mapping.service';
import { ProjectService } from './project.service';
import { TableConfigBuilder } from './table-config-builder.service';
export type StatusType =
    | 'mapping_in_progress'
    | 'reassignment_in_progress'
    | 'failed'
    | 'completed'
    | 'partially_completed';

declare var ap: any;

@Injectable({
    providedIn: 'root',
})
export class ReassignmentService {
    projectService = inject(ProjectService);
    mappingService = inject(MappingService);

    private _casesToReassign: CaseRootInstance[] = [];
    private _inboxType: InboxType = 'myTasks';
    private _inboxColumns: any = {};
    private _reassignConfig: any = {};
    private _inboxHooks: any = {};
    currentTableClass = '';

    get casesToReassign() {
        return this._casesToReassign;
    }

    set casesToReassign(casesToReassign: CaseRootInstance[]) {
        this._casesToReassign = casesToReassign;
    }

    get inboxType() {
        return this._inboxType;
    }

    set inboxType(type: InboxType) {
        this._inboxType = type;
    }

    requiredColumns(array: any) {
        const requiredMappings = [
            {
                mapping: '$."taskId"',
                mappingType: 'task_instance',
                label: '{{lang.default-reassign-taskId}}',
            },
            { mapping: 'cid', mappingType: 'case_instance', label: '{{lang.default-reassign-caseId}}' },
        ];
        const filteredArray = array.filter(
            (item: any) =>
                !requiredMappings.some(
                    (required) => required.mapping === item.mapping && required.mappingType === item.mappingType,
                ),
        );
        return [...requiredMappings, ...filteredArray];
    }

    get inboxColumns() {
        return this._inboxColumns;
    }

    set inboxColumns(config: any) {
        this._inboxColumns = this.requiredColumns(config);
    }

    get reassignConfig() {
        return this.reassignConfig;
    }

    set reassignConfig(config: any) {
        this._reassignConfig = config;
    }

    get setReassignmentOperationId() {
        return this._currentOperationId;
    }

    private _currentOperationId = '';

    set operationId(id: string) {
        this._currentOperationId = id;
    }

    get operationId() {
        return this._currentOperationId;
    }

    get inboxHooks(): any {
        return this._inboxHooks;
    }

    set inboxHooks(hooks: any) {
        this._inboxHooks = hooks;
    }

    private _status: StatusType = 'mapping_in_progress';
    get status(): StatusType {
        return this._status;
    }

    set status(status: StatusType) {
        this._status = status;
    }

    get total() {
        return this._casesToReassign.length;
    }

    allCasesHaveAssignedTo(): boolean {
        return this._casesToReassign.every((caseRootInstance: CaseRootInstance) => {
            return (
                caseRootInstance['assignedTo'] &&
                typeof caseRootInstance['assignedTo'] === 'string' &&
                caseRootInstance['assignedTo'].trim() !== '' &&
                caseRootInstance['assignedTo'] !== 'Unknown' &&
                caseRootInstance['assignedTo'].length > 0
            );
        });
    }

    private filterCasesByGroup(cases: CaseRootInstance[], group: string): CaseRootInstance[] {
        return cases.filter((caseRootInstance: CaseRootInstance) => {
            return caseRootInstance.taskInstance.metadata.groups?.some(
                (groupItem: { name: string }) => groupItem.name === group,
            );
        });
    }

    listGroupsNUsers(): {
        grouped: { group: string; cases: CaseRootInstance[] }[];
        owned: CaseRootInstance[];
    } {
        const grouped: { group: string; cases: CaseRootInstance[] }[] = [];
        const owned: CaseRootInstance[] = [];

        function hasOwner(taskInstance: { metadata: { taskOwner: string | null } }) {
            return (
                taskInstance &&
                taskInstance.metadata &&
                typeof taskInstance.metadata.taskOwner === 'string' &&
                taskInstance.metadata.taskOwner.trim() !== ''
            );
        }

        this._casesToReassign.forEach((caseRootInstance: CaseRootInstance) => {
            if (!hasOwner(caseRootInstance.taskInstance)) {
                if (caseRootInstance.taskInstance.metadata.groups) {
                    caseRootInstance.taskInstance.metadata.groups.forEach((group: { name: string }) => {
                        const groupIndex = grouped.findIndex((g) => g.group === group.name);
                        if (groupIndex !== -1) {
                            grouped[groupIndex].cases.push(caseRootInstance);
                        } else {
                            grouped.push({ group: group.name, cases: [caseRootInstance] });
                        }
                    });
                }
            }
        });

        this._casesToReassign.forEach((caseRootInstance: CaseRootInstance) => {
            if (hasOwner(caseRootInstance.taskInstance)) {
                owned.push(caseRootInstance);
            }
        });
        return {
            grouped: grouped,
            owned: owned,
        };
    }

    private filterCasesByCidNTaskIdArray(
        cases: { cid: string | number; taskId: number | string }[],
    ): CaseRootInstance[] {
        return this._casesToReassign.filter((caseRootInstance: CaseRootInstance) => {
            return cases.some(
                (caseItem) =>
                    caseItem.cid === caseRootInstance.caseInstance.cid &&
                    caseItem.taskId === caseRootInstance.taskInstance.taskId,
            );
        });
    }

    buildReassignmentTable(options: ReassignmentTableOptions): TableConfigBuilder {
        if ('caseIdArray' in options && options.caseIdArray && options?.caseIdArray?.length > 0) {
            const filteredCases = this.filterCasesByCidNTaskIdArray(options.caseIdArray);
            const tableConfig = new TableConfigBuilder()
                .setLabel('Others')
                .setSelectable(false)
                .addColumns(this._inboxColumns)
                .addRows(filteredCases)
                .setBulkButtonConfig({
                    columnLabel: 'Bulk',
                    buttonType: 'button',
                    buttonLabel: 'default-reassign-buttonBulkAssign',
                    outlined: true,
                    icon: 'pi pi-angle-down',
                    iconPosition: 'right',
                    ...options?.bulkActionOptions,
                })
                .setClearAllButtonConfig({
                    columnLabel: 'Clear',
                    buttonType: 'button',
                    buttonLabel: 'default-reassign-buttonClearSelection',
                    outlined: true,
                    text: true,
                    disabled: true,
                    ...options?.clearAllActionOptions,
                })
                .setActionButtonConfig({
                    columnLabel: 'default-reassign-assignedTo',
                    buttonType: 'button',
                    buttonLabel: 'default-reassign-buttonAssign',
                    labelMapping: 'assignedTo',
                    outlined: true,
                    icon: 'pi pi-angle-down',
                    iconPosition: 'right',
                })
                .addRowModifier(async (row) => {
                    const modifiedRow = { ...row };
                    let newRow: any = {};
                    for (const column of this._inboxColumns) {
                        newRow[column.mapping] = await this.mappingService.updateValue(modifiedRow, column);
                    }
                    newRow['currentAssignedTo'] =
                        modifiedRow['assignedTo'] || this.deriveOwner(modifiedRow['taskInstance']);
                    newRow['assignedTo'] = modifiedRow['assignedTo'];
                    return newRow;
                })
                .addColumnModifier((col) => {
                    return col;
                });
            return tableConfig;
        }

        if ('groupName' in options && options.groupName) {
            options.groupName = options.groupName.trim();

            options.groupName = options.groupName.trim();

            const groupCases = this.filterCasesByGroup(this._casesToReassign, options.groupName);
            const tableConfig = new TableConfigBuilder()
                .setLabel(options.groupName)
                .setSelectable(false)
                .setBulkButtonConfig({
                    columnLabel: 'Bulk',
                    buttonType: 'button',
                    buttonLabel: 'default-reassign-buttonBulkAssign',
                    outlined: true,
                    icon: 'pi pi-angle-down',
                    iconPosition: 'right',
                    ...options?.bulkActionOptions,
                })
                .setClearAllButtonConfig({
                    columnLabel: 'Clear',
                    buttonType: 'button',
                    buttonLabel: 'default-reassign-buttonClearSelection',
                    outlined: true,
                    text: true,
                    disabled: true,
                    ...options?.clearAllActionOptions,
                })
                .setActionButtonConfig({
                    columnLabel: 'default-reassign-assignedTo',
                    buttonType: 'button',
                    buttonLabel: 'default-reassign-buttonAssign',
                    labelMapping: 'assignedTo',
                    icon: 'pi pi-angle-down',
                    iconPosition: 'right',
                    outlined: true,
                })
                .addColumns(this._inboxColumns)
                .addRows(groupCases)
                .addRowModifier(async (row) => {
                    const modifiedRow = { ...row };
                    let newRow: any = {};
                    for (const column of this._inboxColumns) {
                        const { mapping, mappingType } = column;
                        newRow[column.mapping] = await this.mappingService.updateValue(modifiedRow, {
                            mapping,
                            mappingType,
                        });
                    }
                    newRow['currentAssignedTo'] =
                        modifiedRow['assignedTo'] || this.deriveOwner(modifiedRow['taskInstance']);
                    newRow['assignedTo'] = modifiedRow['assignedTo'];
                    return newRow;
                })
                .addColumnModifier((col) => {
                    // if (col.columnName === 'assignedTo') {
                    //     col.hide = true;
                    // }
                    return col;
                });
            return tableConfig;
        }

        return new TableConfigBuilder();
    }

    buildBeforeReassignmentSummaryTable(): TableConfigBuilder {
        const tableConfig = new TableConfigBuilder()
            .setLabel('Before Reassignment Summary')
            .setSelectable(false)
            .addColumns(this._inboxColumns)
            .addColumn({
                columnName: '{{lang.default-reassign-previousAssignee}}',
                mapping: 'currentAssignedTo',
                enableSort: false,
                hide: false,
                cellStyle: { background: '#FFFAF2' },
                frozen: false,
                alignFrozen: 'right',
            })
            .addColumn({
                columnName: '{{lang.default-reassign-newAssignee}}',
                mapping: 'assignedTo',
                enableSort: false,
                hide: false,
                cellStyle: { background: '#F2FFF6' },
                frozen: false,
                alignFrozen: 'right',
            })
            .addRows(this._casesToReassign)
            .addRowModifier(async (row) => {
                const modifiedRow = { ...row };
                let newRow: any = {};
                for (const column of this._inboxColumns) {
                    const { mapping, mappingType } = column;
                    newRow[column.mapping] = await this.mappingService.updateValue(modifiedRow, {
                        mapping,
                        mappingType,
                    });
                }
                newRow['currentAssignedTo'] = this.deriveOwner(modifiedRow['taskInstance']);
                newRow['assignedTo'] = modifiedRow['assignedTo'];
                return newRow;
            });
        return tableConfig;
    }

    buildAfterReassignmentSummaryTable(
        failedCases: {
            cid: string;
            taskId: string;
            [key: string]: any;
        }[],
    ): TableConfigBuilder {
        const columns: TableColumnConfig[] = [
            { columnName: '{{lang.default-reassign-status}}', mapping: 'status', enableSort: true, hide: false },
            {
                columnName: '{{lang.default-reassign-assignedTo}}',
                mapping: 'assignedTo',
                enableSort: true,
                hide: false,
            },
            {
                columnName: '{{lang.default-reassign-previousAssignee}}',
                mapping: 'currentAssignedTo',
                enableSort: false,
                hide: false,
                cellStyle: { background: '#FFFAF2' },
            },
            {
                columnName: '{{lang.default-reassign-newAssignee}}',
                mapping: 'assignedTo',
                enableSort: false,
                hide: false,
                tooltipText: '{{lang.default-reassign-tooltipAllocationUnsussessful}}',
                cellStyle: { background: '#F2FFF6' },
                tooltipClass: 'color-warning',
            },
            {
                columnName: '{{lang.default-reassign-reasonForFailure}}',
                mapping: 'assignmentStatus',
                enableSort: false,
                hide: false,
                frozen: false,
                alignFrozen: 'right',
            },
        ];

        let displayFailedCases = this._casesToReassign.filter((caseInstance: CaseRootInstance) => {
            return (
                failedCases.find(
                    (v: any) =>
                        v.cid == caseInstance.caseInstance.cid &&
                        v.taskId == caseInstance.taskInstance.taskId &&
                        v.status == 'failed',
                ) !== undefined
            );
        });

        // map assignmentStatus
        displayFailedCases = displayFailedCases.map((caseInstance: CaseRootInstance) => {
            const failedSummary = failedCases.find((v: any) => {
                return (
                    v.cid == caseInstance.caseInstance.cid &&
                    v.taskId == caseInstance.taskInstance.taskId &&
                    v.status == 'failed'
                );
            });
            return {
                ...caseInstance,
                assignmentStatus: failedSummary ? failedSummary['assignmentStatus'] : null,
                status: failedSummary ? failedSummary['status'] : null,
            };
        });

        this._casesToReassign = displayFailedCases;

        return new TableConfigBuilder()
            .setLabel('After Reassignment Summary')
            .setSelectable(false)
            .addColumns([...this._inboxColumns, ...columns])
            .addRows(displayFailedCases)
            .addRowModifier(async (row) => {
                const modifiedRow = { ...row };
                let newRow: any = {};
                for (const column of this._inboxColumns) {
                    const { mapping, mappingType } = column;
                    newRow[column.mapping] = await this.mappingService.updateValue(modifiedRow, {
                        mapping,
                        mappingType,
                    });
                }
                newRow['currentAssignedTo'] = this.deriveOwner(modifiedRow['taskInstance']);
                newRow['assignedTo'] = modifiedRow['assignedTo'];
                newRow = {
                    ...newRow,
                    ...modifiedRow,
                };
                return newRow;
            });
    }

    reassignCasesTo(cases: CaseRootInstance[], user: string) {
        for (let i = 0; i < cases.length; i++) {
            const caseRootInstance = cases[i];
            const index = this._casesToReassign.findIndex(
                (caseInstance) =>
                    (caseInstance?.caseInstance?.cid == caseRootInstance?.caseInstance?.cid &&
                        caseInstance?.taskInstance?.taskId == caseRootInstance?.taskInstance?.taskId) ||
                    // @ts-ignore
                    (caseRootInstance['$."taskId"'] == caseInstance.taskInstance.taskId &&
                        // @ts-ignore
                        caseRootInstance['cid'] == caseInstance.caseInstance.cid),
            );
            if (index !== -1) {
                this._casesToReassign[index]['assignedTo'] = user;
            }
        }
    }

    private deriveOwner(taskInstance: {
        taskOwner?: string;
        metadata?: { groups?: { name: string }[]; taskOwner?: string | null };
    }): string {
        if (taskInstance?.metadata?.taskOwner) {
            return taskInstance.metadata.taskOwner;
        }
        if (Array.isArray(taskInstance?.metadata?.groups) && taskInstance?.metadata?.groups.length > 0) {
            const validGroups = taskInstance.metadata.groups.filter(
                (group) => typeof group.name === 'string' && group.name.trim() !== '',
            );
            const groupNames = validGroups.map((group) => group.name).join(', ');
            if (groupNames) {
                return groupNames;
            }
        }
        return 'Unknown';
    }

    formatReassignmentDataForBulkOperation(failed?: boolean, comment?: string): BulkReassignmentRequest {
        if (failed) {
            const failedCases = this._casesToReassign.filter((caseInstance: CaseRootInstance) => {
                return caseInstance['status'] === 'failed';
            });
            return {
                projectId: this.projectService.fetchCurrentProject().projectId,
                strategy: 'reassign',
                comment: comment,
                data: failedCases.map((caseRootInstance: CaseRootInstance) => {
                    return {
                        cid: caseRootInstance.caseInstance.cid,
                        taskId: caseRootInstance.taskInstance.taskId,
                        assignedTo: caseRootInstance['assignedTo'],
                        currentUser: this.deriveOwner(caseRootInstance.taskInstance),
                        assignmentStatus: 'pending',
                        status: 'in_progress',
                        operationId: this.operationId,
                        createdAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString(),
                        processId: caseRootInstance.taskInstance?.parentProcessInstanceId,
                        containerId: caseRootInstance.taskInstance?.metadata?.containerId,
                        forceDelegate: this._reassignConfig?.forceDelegate ?? false,
                        currentGroup:
                            caseRootInstance.taskInstance?.metadata?.groups?.map((v) => v.name).join(', ') || '',
                    };
                }),
            };
        }
        const bulkReassignmentRequest: BulkReassignmentRequest = {
            projectId: this.projectService.fetchCurrentProject().projectId,
            strategy: 'reassign',
            comment: comment,
            data: this.casesToReassign.map((caseRootInstance: CaseRootInstance) => {
                return {
                    cid: caseRootInstance.caseInstance.cid,
                    taskId: caseRootInstance.taskInstance.taskId,
                    assignedTo: caseRootInstance['assignedTo'],
                    currentUser: this.deriveOwner(caseRootInstance.taskInstance),
                    assignmentStatus: 'pending',
                    status: 'in_progress',
                    operationId: this.operationId,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                    processId: caseRootInstance.taskInstance?.parentProcessInstanceId,
                    containerId: caseRootInstance.taskInstance?.metadata?.containerId,
                    forceDelegate: this._reassignConfig?.forceDelegate ?? false,
                    currentGroup: caseRootInstance.taskInstance?.metadata?.groups?.map((v) => v.name).join(', ') || '',
                };
            }),
        };

        return bulkReassignmentRequest;
    }

    async executeAfterViewInitAction(task: CustomCodeEventDetail) {
        task.operation = 'reassignment';
        task.taskType = this.inboxType;
        const actions = this._inboxHooks?.afterViewInit?.triggers || [];
        const triggerResult: any = [];
        const params = { eventDetail: task };
        for (const action of actions) {
            try {
                const emmitLoaderEvent = true;
                const responsePromise = ap.trigger(action, params, triggerResult, emmitLoaderEvent);
                if (!action.isAsync) {
                    const response = await responsePromise;
                    triggerResult.push({ action, response: response?.data });
                    await this.handleActionResponse(action, response, triggerResult);
                } else {
                    triggerResult.push({ action, response: responsePromise });
                    responsePromise.then(async (res: any) => {
                        await this.handleActionResponse(action, res, triggerResult);
                    });
                }
            } catch (e) {
                console.error(e);
                if (action.stopExecution && !action.isAsync) {
                    return;
                }
            }
        }
    }

    private async handleActionResponse(action: Action, response: any, triggerResult: any) {
        if (action) {
            ap.toast.toastMessage(action, response?.status, triggerResult);
        }
    }
}
